import React, { useCallback, useEffect, useRef, useState } from 'react';
import { X, Check, Loader2, Eye, RotateCcw, Frame, Star, Grid3x3 } from 'lucide-react';
import {
  VerticalsOptions,
  DEFAULT_VERTICALS,
  isIdentityVerticals,
  warpVerticals,
} from '../services/verticals';
import { getVerticalsDefault, setVerticalsDefault } from '../services/preferences';

interface VerticalsEditorProps {
  imageUrl: string;
  /** Save the chosen correction; parent applies it at full resolution. */
  onSave: (options: VerticalsOptions) => void;
  onCancel: () => void;
  isSaving?: boolean;
}

const PREVIEW_MAX = 1400;

const VerticalsEditor: React.FC<VerticalsEditorProps> = ({
  imageUrl,
  onSave,
  onCancel,
  isSaving = false,
}) => {
  const [options, setOptions] = useState<VerticalsOptions>(() =>
    getVerticalsDefault()
  );
  const [showBefore, setShowBefore] = useState(false);
  const [showGrid, setShowGrid] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const [savedDefault, setSavedDefault] = useState(false);

  const previewCanvasRef = useRef<HTMLCanvasElement>(null);
  const baseImageDataRef = useRef<ImageData | null>(null);

  // --- Load image once into a downscaled preview buffer. ---
  useEffect(() => {
    let cancelled = false;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      if (cancelled) return;
      const w = img.naturalWidth;
      const h = img.naturalHeight;
      const scale = Math.min(1, PREVIEW_MAX / Math.max(w, h));
      const pw = Math.max(1, Math.round(w * scale));
      const ph = Math.max(1, Math.round(h * scale));
      const off = document.createElement('canvas');
      off.width = pw;
      off.height = ph;
      const offCtx = off.getContext('2d', { willReadFrequently: true });
      if (!offCtx) {
        setLoadError('Could not initialise the editor canvas.');
        return;
      }
      offCtx.drawImage(img, 0, 0, pw, ph);
      try {
        baseImageDataRef.current = offCtx.getImageData(0, 0, pw, ph);
      } catch {
        setLoadError('This image could not be loaded for editing (CORS).');
        return;
      }
      setReady(true);
    };
    img.onerror = () => {
      if (!cancelled) setLoadError('Failed to load the image for editing.');
    };
    img.src = imageUrl;
    return () => {
      cancelled = true;
    };
  }, [imageUrl]);

  // --- Render live preview whenever options / before toggle change. ---
  const renderPreview = useCallback(() => {
    const base = baseImageDataRef.current;
    const canvas = previewCanvasRef.current;
    if (!base || !canvas) return;
    if (canvas.width !== base.width || canvas.height !== base.height) {
      canvas.width = base.width;
      canvas.height = base.height;
    }
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    if (showBefore || isIdentityVerticals(options)) {
      ctx.putImageData(base, 0, 0);
    } else {
      ctx.putImageData(warpVerticals(base, options), 0, 0);
    }
  }, [options, showBefore]);

  useEffect(() => {
    if (ready) renderPreview();
  }, [ready, renderPreview]);

  const setField = <K extends keyof VerticalsOptions>(
    key: K,
    value: VerticalsOptions[K]
  ) => setOptions((o) => ({ ...o, [key]: value }));

  const reset = () => setOptions((o) => ({ ...DEFAULT_VERTICALS, autoFit: o.autoFit }));

  const handleSaveDefault = () => {
    setVerticalsDefault(options);
    setSavedDefault(true);
    window.setTimeout(() => setSavedDefault(false), 1800);
  };

  const dirty = !isIdentityVerticals(options);

  // Rendered via a plain function (not a nested component) so the range inputs
  // keep their identity across renders and dragging stays smooth.
  const renderSlider = (
    label: string,
    field: 'vertical' | 'horizontal' | 'rotate',
    min: number,
    max: number,
    step: number,
    format: (v: number) => string
  ) => (
    <div key={field}>
      <div className="flex items-center justify-between mb-2">
        <label className="text-sm font-medium text-slate-200">{label}</label>
        <span className="text-sm font-mono text-brand-300">
          {format(options[field])}
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={options[field]}
        onChange={(e) => setField(field, Number(e.target.value))}
        onDoubleClick={() => setField(field, 0)}
        className="w-full accent-brand-500 cursor-pointer"
      />
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 bg-slate-950/90 backdrop-blur-sm flex flex-col">
      {/* Header */}
      <div className="h-14 flex items-center justify-between px-4 border-b border-slate-800 bg-slate-900/80">
        <div className="flex items-center gap-2">
          <Frame className="w-5 h-5 text-brand-400" />
          <h2 className="text-white font-semibold">Vertical &amp; lens correction</h2>
          <span className="text-xs text-slate-500 hidden sm:inline ml-1">
            Straighten converging walls &amp; level the horizon
          </span>
        </div>
        <button
          onClick={onCancel}
          className="p-2 text-slate-400 hover:text-white transition-colors"
          title="Close"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="flex-1 flex flex-col lg:flex-row min-h-0">
        {/* Preview */}
        <div className="flex-1 min-h-0 flex items-center justify-center p-4 overflow-auto">
          {loadError ? (
            <div className="text-center max-w-sm">
              <p className="text-red-400 font-medium mb-2">{loadError}</p>
              <p className="text-slate-500 text-sm">
                Try downloading the image and re-uploading, or contact support.
              </p>
            </div>
          ) : !ready ? (
            <div className="flex flex-col items-center gap-3 text-slate-400">
              <Loader2 className="w-8 h-8 animate-spin text-brand-400" />
              <span className="text-sm">Loading image…</span>
            </div>
          ) : (
            <div className="relative max-w-full max-h-full">
              <canvas
                ref={previewCanvasRef}
                className="max-w-full max-h-[calc(100vh-160px)] object-contain rounded-lg shadow-2xl bg-slate-900"
              />
              {showGrid && (
                <div
                  className="absolute inset-0 pointer-events-none rounded-lg overflow-hidden"
                  aria-hidden
                >
                  <div className="absolute inset-0 grid grid-cols-4 grid-rows-4">
                    {Array.from({ length: 16 }).map((_, i) => (
                      <div
                        key={i}
                        className="border border-brand-300/25"
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Controls */}
        <div className="w-full lg:w-[360px] flex-shrink-0 border-t lg:border-t-0 lg:border-l border-slate-800 bg-slate-900/80 p-4 flex flex-col gap-5 overflow-y-auto">
          {renderSlider(
            'Vertical',
            'vertical',
            -1,
            1,
            0.01,
            (v) => `${v > 0 ? '+' : ''}${Math.round(v * 100)}`
          )}
          {renderSlider(
            'Horizontal',
            'horizontal',
            -1,
            1,
            0.01,
            (v) => `${v > 0 ? '+' : ''}${Math.round(v * 100)}`
          )}
          {renderSlider(
            'Rotation',
            'rotate',
            -15,
            15,
            0.1,
            (v) => `${v > 0 ? '+' : ''}${v.toFixed(1)}°`
          )}
          <p className="text-[11px] text-slate-500 -mt-3">
            Double-click a slider to reset it to zero.
          </p>

          {/* Auto-fit */}
          <label className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg bg-slate-800/60 border border-slate-700 cursor-pointer">
            <span className="text-sm text-slate-200">
              Auto-crop edges
              <span className="block text-[11px] text-slate-500">
                Zoom in slightly to remove blank corners
              </span>
            </span>
            <input
              type="checkbox"
              checked={options.autoFit}
              onChange={(e) => setField('autoFit', e.target.checked)}
              className="w-4 h-4 accent-brand-500 cursor-pointer"
            />
          </label>

          {/* Toggles row */}
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => setShowGrid((g) => !g)}
              className={`flex items-center justify-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors ${
                showGrid
                  ? 'bg-brand-500 text-white'
                  : 'bg-slate-800 hover:bg-slate-700 text-slate-300'
              }`}
            >
              <Grid3x3 className="w-4 h-4" />
              Grid
            </button>
            <button
              onClick={reset}
              className="flex items-center justify-center gap-2 px-3 py-2 text-sm rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 transition-colors"
            >
              <RotateCcw className="w-4 h-4" />
              Reset
            </button>
          </div>

          {/* Before / after */}
          <button
            onMouseDown={() => setShowBefore(true)}
            onMouseUp={() => setShowBefore(false)}
            onMouseLeave={() => setShowBefore(false)}
            onTouchStart={() => setShowBefore(true)}
            onTouchEnd={() => setShowBefore(false)}
            className={`flex items-center justify-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors ${
              showBefore
                ? 'bg-brand-500 text-white'
                : 'bg-slate-800 hover:bg-slate-700 text-slate-300'
            }`}
          >
            <Eye className="w-4 h-4" />
            Hold to compare original
          </button>

          {/* Save as default */}
          <button
            onClick={handleSaveDefault}
            className="flex items-center justify-center gap-2 px-3 py-2 text-sm rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 transition-colors"
          >
            {savedDefault ? (
              <>
                <Check className="w-4 h-4 text-emerald-400" />
                Saved as your default
              </>
            ) : (
              <>
                <Star className="w-4 h-4" />
                Save as my default
              </>
            )}
          </button>

          <div className="flex-1" />

          {/* Actions */}
          <div className="flex gap-2 pt-2 border-t border-slate-800">
            <button
              onClick={onCancel}
              disabled={isSaving}
              className="flex-1 px-4 py-2.5 text-sm font-medium rounded-lg bg-slate-800 hover:bg-slate-700 text-white transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={() => onSave(options)}
              disabled={isSaving || !ready || !dirty}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg bg-brand-500 hover:bg-brand-600 text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving…
                </>
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  Save
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerticalsEditor;
